// src/pages/Auth/hooks/useSession.js

import { useCallback, useEffect, useRef } from 'react';
import { SESSION_CONFIG, STORAGE_KEYS } from '../constants';

export const useSession = () => {
  const activityTimerRef = useRef(null);

  const initializeSession = useCallback((shouldRemember) => {
    const sessionConfig = {
      type: shouldRemember ? 'extended' : 'short',
      startTime: Date.now(),
      expiryTime: Date.now() + (shouldRemember ? SESSION_CONFIG.EXTENDED_SESSION : SESSION_CONFIG.SHORT_SESSION),
      lastActivity: Date.now(),
    };
    
    if (shouldRemember) {
      localStorage.setItem(STORAGE_KEYS.SESSION_CONFIG, JSON.stringify(sessionConfig));
      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
    } else {
      sessionStorage.setItem(STORAGE_KEYS.SESSION_CONFIG, JSON.stringify(sessionConfig));
      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'false');
      localStorage.setItem(STORAGE_KEYS.SHOW_STAY_LOGGED_IN_PROMPT, 'true');
    }
  }, []);

  const updateActivity = useCallback(() => {
    const sessionConfigStr = localStorage.getItem(STORAGE_KEYS.SESSION_CONFIG) || 
                            sessionStorage.getItem(STORAGE_KEYS.SESSION_CONFIG);
    
    if (sessionConfigStr) {
      try {
        const sessionConfig = JSON.parse(sessionConfigStr);
        sessionConfig.lastActivity = Date.now();
        const storage = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true' 
          ? localStorage 
          : sessionStorage;
        storage.setItem(STORAGE_KEYS.SESSION_CONFIG, JSON.stringify(sessionConfig));
      } catch (e) {
        console.error('Failed to update session activity:', e);
      }
    }
  }, []);

  const isSessionValid = useCallback(() => {
    const sessionConfigStr = localStorage.getItem(STORAGE_KEYS.SESSION_CONFIG) || 
                            sessionStorage.getItem(STORAGE_KEYS.SESSION_CONFIG);
    
    if (!sessionConfigStr) return false;
    
    try {
      const sessionConfig = JSON.parse(sessionConfigStr);
      return Date.now() < sessionConfig.expiryTime;
    } catch (e) {
      return false;
    }
  }, []);

  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'click'];
    
    const handleActivity = () => {
      updateActivity();
      
      // Reset timer
      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
      }
      
      // Set timeout to check session expiry
      activityTimerRef.current = setTimeout(() => {
        if (!isSessionValid()) {
          // Session expired - trigger logout
          window.dispatchEvent(new CustomEvent('sessionExpired'));
        }
      }, 60000); // Check every minute
    };
    
    events.forEach(event => window.addEventListener(event, handleActivity));
    
    return () => {
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (activityTimerRef.current) {
        clearTimeout(activityTimerRef.current);
      }
    };
  }, [updateActivity, isSessionValid]);

  return { initializeSession, updateActivity, isSessionValid };
};